export interface IndicatorInfo {
  name: string
  /** Shorthand shown on the chart (legend chip) when the entry has no calcParams */
  label: string
  /** Full descriptive name shown (and searched) in the indicator picker */
  fullName: string
  paneType: 'main' | 'sub'
  /** Single-value calcParams so only one line/series is added per click */
  calcParams?: unknown[]
}

/** Indicators that overlay on the price (main) pane */
export const MAIN_INDICATORS: IndicatorInfo[] = [
  { name: 'MA', label: 'MA', fullName: 'Moving Average', paneType: 'main', calcParams: [20] },
  {
    name: 'EMA',
    label: 'EMA',
    fullName: 'Exponential Moving Average',
    paneType: 'main',
    calcParams: [20],
  },
  // SMA is klinecharts' *smoothed* MA: calc is (close*w + prev*(p-w+1))/(p+1).
  // weight=2 collapses exactly to EMA(p), so we use weight=1 to keep it distinct.
  // The weight is an internal smoothing constant — hidden from the legend label.
  {
    name: 'SMA',
    label: 'SMA',
    fullName: 'Smoothed Moving Average',
    paneType: 'main',
    calcParams: [20, 1],
  },
  {
    name: 'BOLL',
    label: 'BOLL',
    fullName: 'Bollinger Bands',
    paneType: 'main',
    calcParams: [20, 2],
  },
  { name: 'SAR', label: 'SAR', fullName: 'Parabolic SAR', paneType: 'main' },
  { name: 'BBI', label: 'BBI', fullName: 'Bull and Bear Index', paneType: 'main' },
  { name: 'VWAP', label: 'VWAP', fullName: 'Volume Weighted Average Price', paneType: 'main' },
  { name: 'PIVOT', label: 'PIVOT', fullName: 'Pivot Points', paneType: 'main' },
  // Sizing readout — calcParams are [direction, risk$, source, maPeriod,
  // maxPosition$] (see positionSizeIndicator.ts / the 2026-08-24 sizing spec).
  {
    name: 'POSITION_SIZE',
    label: 'SIZE',
    fullName: 'Position Sizing',
    paneType: 'main',
    calcParams: ['long', 100, 'manual', 9, 0],
  },
  // VPVR is painted by a custom draw (utils/volumeProfileIndicator.ts) from the
  // visible bars; calcParams are [rows, widthPct, valueAreaPct, split, placement].
  {
    name: 'VPVR',
    label: 'VPVR',
    fullName: 'Volume Profile',
    paneType: 'main',
    calcParams: [24, 30, 70, true, 'left'],
  },
  // Session Volume Profile — one profile per trading day, pinned to each
  // session's START edge by default.
  {
    name: 'VPSV',
    label: 'Session VP',
    fullName: 'Volume Profile (Session)',
    paneType: 'main',
    calcParams: [24, 30, 70, true, 'left', 'calendar-day'],
  },
  // Rolling X-day Volume Profile — one profile over the last N session days of
  // loaded data, anchored to the newest bar (not the viewport).
  {
    name: 'VPXD',
    label: 'VP Days',
    fullName: 'Volume Profile (X Days)',
    paneType: 'main',
    calcParams: [24, 30, 70, true, 'left', 2, 'calendar-day', 'latest-data'],
  },
  // Higher-timeframe projection — a stepped series from a coarser INTRADAY
  // timeframe. calcParams are [targetPeriod, field].
  {
    name: 'HTF',
    label: 'HTF',
    fullName: 'Higher Timeframe',
    paneType: 'main',
    calcParams: ['1H', 'candles', 'close', true],
  },
  // Extended MA family + bands — registered in utils/maBandIndicators.ts
  {
    name: 'WMA',
    label: 'WMA',
    fullName: 'Weighted Moving Average',
    paneType: 'main',
    calcParams: [20],
  },
  {
    name: 'HMA',
    label: 'HMA',
    fullName: 'Hull Moving Average',
    paneType: 'main',
    calcParams: [16],
  },
  {
    name: 'DEMA',
    label: 'DEMA',
    fullName: 'Double Exponential Moving Average',
    paneType: 'main',
    calcParams: [20],
  },
  {
    name: 'TEMA',
    label: 'TEMA',
    fullName: 'Triple Exponential Moving Average',
    paneType: 'main',
    calcParams: [20],
  },
  {
    name: 'TMA',
    label: 'TMA',
    fullName: 'Triangular Moving Average',
    paneType: 'main',
    calcParams: [20],
  },
  {
    name: 'KAMA',
    label: 'KAMA',
    fullName: 'Kaufman Adaptive Moving Average',
    paneType: 'main',
    calcParams: [10],
  },
  {
    name: 'VIDYA',
    label: 'VIDYA',
    fullName: 'Variable Index Dynamic Average',
    paneType: 'main',
    calcParams: [14, 9],
  },
  {
    name: 'ZLEMA',
    label: 'ZLEMA',
    fullName: 'Zero-Lag Exponential Moving Average',
    paneType: 'main',
    calcParams: [14],
  },
  { name: 'MGD', label: 'MGD', fullName: 'McGinley Dynamic', paneType: 'main', calcParams: [14] },
  {
    name: 'WWS',
    label: 'WWS',
    fullName: 'Welles Wilder Smoothing',
    paneType: 'main',
    calcParams: [14],
  },
  {
    name: 'KC',
    label: 'KC',
    fullName: 'Keltner Channel',
    paneType: 'main',
    calcParams: [20, 10, 2],
  },
  { name: 'DC', label: 'DC', fullName: 'Donchian Channel', paneType: 'main', calcParams: [20] },
  { name: 'ENV', label: 'ENV', fullName: 'MA Envelopes', paneType: 'main', calcParams: [20, 2.5] },
  { name: 'HLB', label: 'HLB', fullName: 'High Low Bands', paneType: 'main', calcParams: [14] },
  {
    name: 'APZ',
    label: 'APZ',
    fullName: 'Adaptive Price Zone',
    paneType: 'main',
    calcParams: [21, 2],
  },
  // Linear regression family — registered in utils/talibIndicators.ts
  {
    name: 'LINREG',
    label: 'LINREG',
    fullName: 'Linear Regression',
    paneType: 'main',
    calcParams: [14],
  },
  {
    name: 'LRI',
    label: 'LRI',
    fullName: 'Linear Regression Intercept',
    paneType: 'main',
    calcParams: [14],
  },
  {
    name: 'TSF',
    label: 'TSF',
    fullName: 'Time Series Forecast',
    paneType: 'main',
    calcParams: [14],
  },
]

/** Indicators that render in their own sub-pane */
export const SUB_INDICATORS: IndicatorInfo[] = [
  { name: 'VOL', label: 'VOL', fullName: 'Volume', paneType: 'sub' },
  {
    name: 'MACD',
    label: 'MACD',
    fullName: 'Moving Average Convergence Divergence',
    paneType: 'sub',
  },
  { name: 'KDJ', label: 'KDJ', fullName: 'KDJ Stochastic', paneType: 'sub' },
  { name: 'RSI', label: 'RSI', fullName: 'Relative Strength Index', paneType: 'sub' },
  { name: 'BIAS', label: 'BIAS', fullName: 'Bias (Price vs MA)', paneType: 'sub' },
  { name: 'BRAR', label: 'BRAR', fullName: 'Buying/Selling Sentiment', paneType: 'sub' },
  { name: 'CCI', label: 'CCI', fullName: 'Commodity Channel Index', paneType: 'sub' },
  { name: 'DMI', label: 'DMI', fullName: 'Directional Movement Index', paneType: 'sub' },
  { name: 'CR', label: 'CR', fullName: 'CR Price Strength', paneType: 'sub' },
  { name: 'PSY', label: 'PSY', fullName: 'Psychological Line', paneType: 'sub' },
  { name: 'DMA', label: 'DMA', fullName: 'Difference of Moving Averages', paneType: 'sub' },
  { name: 'TRIX', label: 'TRIX', fullName: 'Triple EMA Rate of Change', paneType: 'sub' },
  { name: 'OBV', label: 'OBV', fullName: 'On-Balance Volume', paneType: 'sub' },
  { name: 'VR', label: 'VR', fullName: 'Volume Ratio', paneType: 'sub' },
  { name: 'WR', label: 'WR', fullName: 'Williams %R', paneType: 'sub' },
  { name: 'MTM', label: 'MTM', fullName: 'Momentum', paneType: 'sub' },
  { name: 'EMV', label: 'EMV', fullName: 'Ease of Movement', paneType: 'sub' },
  { name: 'ROC', label: 'ROC', fullName: 'Rate of Change', paneType: 'sub' },
  { name: 'PVT', label: 'PVT', fullName: 'Price Volume Trend', paneType: 'sub' },
  { name: 'AO', label: 'AO', fullName: 'Awesome Oscillator', paneType: 'sub' },
  // TA-Lib parity set — registered in utils/talibIndicators.ts
  { name: 'PPO', label: 'PPO', fullName: 'Percentage Price Oscillator', paneType: 'sub' },
  { name: 'APO', label: 'APO', fullName: 'Absolute Price Oscillator', paneType: 'sub' },
  { name: 'BOP', label: 'BOP', fullName: 'Balance of Power', paneType: 'sub' },
  { name: 'CMO', label: 'CMO', fullName: 'Chande Momentum Oscillator', paneType: 'sub' },
  { name: 'IMI', label: 'IMI', fullName: 'Intraday Momentum Index', paneType: 'sub' },
  { name: 'ULTOSC', label: 'ULTOSC', fullName: 'Ultimate Oscillator', paneType: 'sub' },
  { name: 'STOCHF', label: 'STOCHF', fullName: 'Stochastic Fast', paneType: 'sub' },
  { name: 'AROON', label: 'AROON', fullName: 'Aroon', paneType: 'sub' },
  { name: 'AROONOSC', label: 'AROONOSC', fullName: 'Aroon Oscillator', paneType: 'sub' },
  { name: 'MFI', label: 'MFI', fullName: 'Money Flow Index', paneType: 'sub' },
  { name: 'STOCHRSI', label: 'STOCHRSI', fullName: 'Stochastic RSI', paneType: 'sub' },
  { name: 'ATR', label: 'ATR', fullName: 'Average True Range', paneType: 'sub' },
  { name: 'NATR', label: 'NATR', fullName: 'Normalized Average True Range', paneType: 'sub' },
  { name: 'AD', label: 'AD', fullName: 'Chaikin Accumulation/Distribution', paneType: 'sub' },
  { name: 'ADOSC', label: 'ADOSC', fullName: 'Chaikin Oscillator', paneType: 'sub' },
  { name: 'LRS', label: 'LRS', fullName: 'Linear Regression Slope', paneType: 'sub' },
  { name: 'LRA', label: 'LRA', fullName: 'Linear Regression Angle', paneType: 'sub' },
]

export const ALL_INDICATORS: IndicatorInfo[] = [...MAIN_INDICATORS, ...SUB_INDICATORS]

interface LegendParamConfig {
  /** calcParams indices to surface, in display order. Empty → bare name. */
  indexes: readonly number[]
  /** Suffix appended to each shown value, e.g. VPXD's day count reads "2D". */
  suffix?: string
  /** Per-value formatter, when a raw `String(value)` would not read well. */
  format?: (value: unknown, index: number) => string
  /** Separator between values. Defaults to a bare comma. */
  separator?: string
  /**
   * Prefix the parenthesised form with the catalog `label` instead of the
   * indicator `name`. Defaults to `name`, which is what VPXD wants (it renders
   * "VPXD(2D)", not "VP Days(2D)"); POSITION_SIZE wants the short "SIZE".
   */
  useLabel?: boolean
}

/**
 * Which calcParams reach the legend label, per indicator. Absent → all of them.
 *
 * The legend is glanceable status, not a settings dump: a param earns its place
 * only if seeing it changes what you understand the plot to be. SMA's internal
 * smoothing weight does not (label reads SMA(20), not SMA(20,1)), and neither do
 * a volume profile's rows / width / value-area / split / placement — those are
 * set once and never read back. VPXD is the exception: its day count decides
 * WHICH volume the profile measures, so it stays, suffixed to read as days.
 */
const LEGEND_PARAMS: Record<string, LegendParamConfig> = {
  SMA: { indexes: [0] },
  // The three lengths are the read; source and average types are set-once.
  MACD: { indexes: [0, 1, 2] },
  VPVR: { indexes: [] },
  // Session start and line breaks change the drawing, not the reading.
  VWAP: { indexes: [] },
  VPSV: { indexes: [] },
  VPXD: { indexes: [5], suffix: 'D' },
  // Target timeframe only. The projection draws the whole coarser bar, so the
  // `field` param names nothing visible (it applies to `line` mode alone), and
  // the display/wicks flags are already obvious from the render.
  HTF: { indexes: [0] },
  // Direction and risk are the two knobs that change what the size MEANS, so
  // they earn the chip; source and MA period do not (the pill carries those).
  POSITION_SIZE: {
    indexes: [0, 1],
    separator: ', ',
    useLabel: true,
    format: (v, i) =>
      i === 0
        ? String(v).toUpperCase()
        : `$${Number(v).toLocaleString('en-US', {
            minimumFractionDigits: Number.isInteger(Number(v)) ? 0 : 2,
            maximumFractionDigits: 2,
          })}`,
  },
}

/** Build the legend label for an indicator, hiding params that aren't worth a
 *  glance. Falls back to the bare catalog label when nothing is left to show. */
export function formatIndicatorLabel(
  name: string,
  params?: unknown[],
  sourceTimeframe?: string,
): string {
  // "· 1H" marks a multi-timeframe instance — two EMAs at different source
  // timeframes must be tellable apart in the legend at a glance.
  const tfSuffix = sourceTimeframe ? ` · ${sourceTimeframe}` : ''
  const base = ALL_INDICATORS.find((i) => i.name === name)?.label ?? name
  if (!params || params.length === 0) return base + tfSuffix
  const cfg = LEGEND_PARAMS[name]
  const shown = cfg
    ? cfg.indexes
        .map((i) => [params[i], i] as const)
        // A truncated/legacy param array must not render "VPXD(undefinedD)".
        .filter(([v]) => v !== undefined && v !== null)
        .map(([v, i]) => `${cfg.format ? cfg.format(v, i) : v}${cfg.suffix ?? ''}`)
    : params
  if (shown.length === 0) return base + tfSuffix
  const prefix = cfg?.useLabel ? base : name
  return `${prefix}(${shown.join(cfg?.separator ?? ',')})${tfSuffix}`
}
